<template>
  <v-card class="p-2" outlined>
    <v-skeleton-loader
      v-bind="attrs"
      type="card-heading, image, actions"
      v-show="loading"
    />

    <card-chart-header
      title="Top Performers"
      subtitle="charts are clickable"
      :getData="getData"
      v-show="!loading"
    />

    <div
      class="position-relative"
      :class="{
        'hide-component': loading,
      }"
      style="min-height: 400px"
    >
      <v-overlay absolute :value="isTopPerformersEmpty">
        <h4>No "Top Performers" data found!</h4>
      </v-overlay>

      <GChart
        type="ColumnChart"
        :data="topPerformers"
        :options="topPerformersOptions"
        :settings="{ packages: ['bar'] }"
        :createChart="(el, google) => new google.charts.Bar(el)"
        @ready="toggleLoading"
        @select="chartOnSelect"
        class="mr-5 mt-5"
        ref="gchart"
        v-if="!isTopPerformersEmpty"
      />
    </div>
  </v-card>
</template>

<script>
import {
  findIndex as _findIndex,
  orderBy as _orderBy,
  take as _take,
  isEmpty as _isEmpty,
  every as _every,
} from 'lodash-es';
import CardChartHeader from '@/components/client-reports/widgets/CardChartHeader.vue';

function defaultTopPerformers() {
  return [
    [
      'Salesperson',
      'Unit Video Completed',
      'Unit Text Completed',
      'VOTD Completed',
      'Days Visited for the last 30 days',
      'Video Watched',
    ],
  ];
}

export default {
  name: 'TopPerformers',

  components: { CardChartHeader },

  props: ['model', 'showChartDialog', 'getData'],

  data() {
    return {
      topPerformers: defaultTopPerformers(),
      topPerformersOptions: {
        chart: {
          title: '',
          subtitle: '',
        },
        bars: 'vertical',
        hAxis: { format: 'decimal' },
        height: 400,
        width: '100%',
        colors: ['#058AC4', '#aaaaaa', '#037b2a', '#F9B421', '#c91f10'],
      },

      chartEvents: {
        select: this.chartOnSelect,
        ready: this.toggleLoading,
      },

      loading: true,

      overlay: false,

      chartsLib: null,
    };
  },
  computed: {
    isTopPerformersEmpty() {
      if (
        _isEmpty(this.model.mostUnitVideosCompleted) &&
        _isEmpty(this.model.mostUnitTextCompleted) &&
        _isEmpty(this.model.mostWatchedVideoOfTheDay) &&
        _isEmpty(this.model.mostDaysVisited) &&
        _isEmpty(this.model.mostVideoWatched)
      ) {
        return true;
      }
      return false;
    },

    chartOptions() {
      if (!this.chartsLib) {
        return null;
      }

      return this.chartsLib.charts.Bar.convertOptions({
        chart: {
          title: '',
          subtitle: '',
        },
        bars: 'vertical',
        hAxis: { format: 'decimal' },
        height: 400,
        width: '100%',
        colors: ['#058AC4', '#ff00fb', '#05ea47', '#ffdd00', '#EC4534'],
      });
    },
  },
  methods: {
    // eslint-disable-next-line consistent-return
    initializePerformers() {
      if (_every(this.model, (item) => _isEmpty(item))) {
        this.overlay = true;
        this.loading = false;
        return;
      }

      this.overlay = false;

      const users = [];

      this.topPerformers = defaultTopPerformers();

      this.model.mostUnitVideosCompleted.forEach((item) => {
        users.push({
          id: item.id,
          name: item.name,
          mostUnitVideosCompleted: item.count,
        });
      });

      this.model.mostUnitTextCompleted.forEach((item) => {
        users.push({
          id: item.id,
          name: item.name,
          mostUnitTextCompleted: item.count,
        });
      });

      this.model.mostWatchedVideoOfTheDay.forEach((item) => {
        const userIndex = _findIndex(users, { id: item.id });

        if (userIndex !== -1) {
          users[userIndex].mostWatchedVideoOfTheDay = item.count;
        } else {
          users.push({
            id: item.id,
            name: item.name,
            mostWatchedVideoOfTheDay: item.count,
          });
        }
      });

      this.model.mostDaysVisited.forEach((item) => {
        const userIndex = _findIndex(users, { id: item.id });

        if (userIndex !== -1) {
          users[userIndex].mostDaysVisited = item.count;
        } else {
          users.push({
            id: item.id,
            name: item.name,
            mostDaysVisited: item.count,
          });
        }
      });

      this.model.mostVideoWatched.forEach((item) => {
        const userIndex = _findIndex(users, { id: item.id });

        if (userIndex !== -1) {
          users[userIndex].mostVideoWatched = item.count;
        } else {
          users.push({
            id: item.id,
            name: item.name,
            mostVideoWatched: item.count,
          });
        }
      });

      this.processUsers(users);
    },
    processUsers(users) {
      let sorted = _orderBy(
        users,
        (item) => {
          let total = 0;

          if (item.mostUnitVideosCompleted !== undefined) {
            total += item.mostUnitVideosCompleted;
          }

          if (item.mostUnitTextCompleted !== undefined) {
            total += item.mostUnitTextCompleted;
          }

          if (item.mostWatchedVideoOfTheDay !== undefined) {
            total += item.mostWatchedVideoOfTheDay;
          }

          if (item.mostDaysVisited !== undefined) {
            total += item.mostDaysVisited;
          }

          if (item.mostVideoWatched !== undefined) {
            total += item.mostVideoWatched;
          }

          return total;
        },
        'desc'
      );

      sorted = _take(sorted, 5);

      sorted.forEach((item) => {
        const user = [];
        user.push(item.name);
        user.push(
          item.mostUnitVideosCompleted !== undefined
            ? item.mostUnitVideosCompleted
            : 0
        );
        user.push(
          item.mostUnitTextCompleted !== undefined
            ? item.mostUnitTextCompleted
            : 0
        );
        user.push(
          item.mostWatchedVideoOfTheDay !== undefined
            ? item.mostWatchedVideoOfTheDay
            : 0
        );
        user.push(
          item.mostDaysVisited !== undefined ? item.mostDaysVisited : 0
        );
        user.push(
          item.mostVideoWatched !== undefined ? item.mostVideoWatched : 0
        );

        this.topPerformers.push(user);
      });

      this.removeLegends();
    },
    removeLegends() {
      const totals = {
        mostUnitVideosCompleted: 0,
        mostUnitTextCompleted: 0,
        mostWatchedVideoOfTheDay: 0,
        mostDaysVisited: 0,
        mostVideoWatched: 0,
      };

      this.topPerformers.slice(1).forEach((item) => {
        totals.mostUnitVideosCompleted += item[1];
        totals.mostUnitTextCompleted += item[2];
        totals.mostWatchedVideoOfTheDay += item[3];
        totals.mostDaysVisited += item[4];
        totals.mostVideoWatched += item[5];
      });

      const result = [];
      const lowCount = 5;
      this.topPerformers.forEach((item) => {
        const user = [item[0]];

        if (totals.mostUnitVideosCompleted >= lowCount) {
          user.push(item[1]);
        }

        if (totals.mostUnitTextCompleted >= lowCount) {
          user.push(item[2]);
        }

        if (totals.mostWatchedVideoOfTheDay >= lowCount) {
          user.push(item[3]);
        }

        if (totals.mostDaysVisited >= lowCount) {
          user.push(item[4]);
        }

        if (totals.mostVideoWatched >= lowCount) {
          user.push(item[5]);
        }

        result.push(user);
      });

      this.topPerformers = result;
    },

    chartOnSelect() {
      const data = this.$refs.gchart.getValidChartData();
      const table = this.$refs.gchart.chartObject;
      const selection = table.getSelection();

      for (let i = 0; i < selection.length; i++) {
        const item = selection[i];
        if (item.row === null && item.column === null) {
          continue;
        }

        const user = data.getValue(selection[0].row, 0);

        this.showChartDialog('topPerformers', user, `Top Performer: ${user}`);
      }
    },

    toggleLoading(chart, google) {
      this.chartsLib = google;
      this.loading = false;
    },
  },
};
</script>
